import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    CircularProgress,
    Alert,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableRow
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { AppButton } from '../../../shared/components/AppButton';
import { userService } from '../../../shared/services/userService';

interface User {
    id: string;
    userId: number;
    name: string;
    email: string | null;
    mobile: string | null;
    phone1: string | null;
    phone2: string | null;
    phone3: string | null;
    address1: string;
    address2: string;
    address3: string;
    city: string;
    state: string | null;
    country: string | null;
    pincode: string;
    area: string;
    status: string;
    isActive: boolean;
    isDeleted: boolean;
    createdAt: string;
    updatedAt: string;
    [key: string]: any;
}

export const UserDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchUser = async () => {
        try {
            setLoading(true);
            setError('');
            const response = await userService.getDetails(id!);
            if (response?.data) {
                const userData = response.data;
                setUser({
                    ...userData,
                    id: userData.id || userData._id || userData.userId
                });
            } else {
                setError('User not found');
            }
        } catch (err) {
            console.error('Error fetching user:', err);
            setError('Failed to load user details');
            setUser(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!id) {
            setError('Invalid user ID');
            setLoading(false);
            return;
        }
        fetchUser();
    }, [id]);

    const handleRoleToggle = async () => {
        if (!user) return;
        try {
            await userService.toggleRole(user.id);
            setUser(prev => prev ? {
                ...prev,
                role: prev.role?.toLowerCase() === 'delivery' ? 'customer' : 'delivery'
            } : null);
        } catch (err: any) {
            console.error('Error toggling role:', err);
            setError(err?.response?.data?.message || 'Failed to toggle role');
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col flex-auto min-w-0">
                <div className="flex flex-auto p-6 sm:p-10 items-center justify-center min-h-[300px]">
                    <CircularProgress />
                </div>
            </div>
        );
    }

    if (error || !user) {
        return (
            <div className="flex flex-col flex-auto min-w-0">
                <div className="p-6 sm:p-10">
                    <AppButton variant="secondary" startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')}>
                        Back to Users
                    </AppButton>
                    <Box mt={2}>
                        <Alert severity="error">{error || 'User not found'}</Alert>
                    </Box>
                </div>
            </div>
        );
    }

    const infoRows: { label: string; value: React.ReactNode }[] = [
        { label: 'Name', value: user.name },
        { label: 'User ID', value: user.userId },
        { label: 'Email', value: user.email },
        { label: 'Mobile', value: user.mobile },
        { label: 'Phone 1', value: user.phone1 },
        { label: 'Phone 2', value: user.phone2 },
        { label: 'Phone 3', value: user.phone3 },
        { label: 'Address 1', value: user.address1 },
        { label: 'Address 2', value: user.address2 },
        { label: 'Address 3', value: user.address3 },
        { label: 'City', value: user.city },
        { label: 'State', value: user.state },
        { label: 'Country', value: user.country },
        { label: 'Pincode', value: user.pincode },
        { label: 'Area', value: user.area },
        {
            label: 'Role', value: (
                <div className="flex items-center gap-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${user.role?.toLowerCase() === 'delivery' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>
                        {user.role || 'customer'}
                    </span>
                    <AppButton
                        variant="secondary"
                        size="small"
                        onClick={handleRoleToggle}
                        className="!text-[10px] !py-1"
                    >
                        Switch to {user.role?.toLowerCase() === 'delivery' ? 'Customer' : 'Delivery'}
                    </AppButton>
                </div>
            )
        },
        { label: 'Status', value: user.status },
        { label: 'Active', value: user.isActive ? 'Yes' : 'No' },
        { label: 'Deleted', value: user.isDeleted ? 'Yes' : 'No' },
        { label: 'Created At', value: user.createdAt ? new Date(user.createdAt).toLocaleString() : null },
        { label: 'Updated At', value: user.updatedAt ? new Date(user.updatedAt).toLocaleString() : null }
    ];

    return (
        <div className="flex flex-col flex-auto min-w-0">
            <div className="flex flex-col sm:flex-row flex-0 sm:items-center sm:justify-between p-6 sm:py-8 sm:px-10 border-b bg-card dark:bg-transparent">
                <div className="flex items-center gap-4">
                    <AppButton variant="secondary" startIcon={<ArrowBackIcon />} onClick={() => navigate('/users')}>
                        Back to Users
                    </AppButton>
                    <h2 className="text-2xl md:text-3xl font-bold tracking-tight">{user.name}</h2>
                </div>
            </div>

            <div className="flex flex-auto p-6 sm:p-10">
                <div className="flex flex-col gap-6 w-full max-w-4xl">
                    <Paper className="p-4 sm:p-6 !shadow-sm !border !border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">User details</h3>
                        <Table size="small">
                            <TableBody>
                                {infoRows.map((row) => (
                                    <TableRow key={row.label}>
                                        <TableCell className="!font-medium !text-gray-600 !w-40">
                                            {row.label}
                                        </TableCell>
                                        <TableCell>{row.value ?? '–'}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </div>
            </div>
        </div>
    );
};
